import { useEffect, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type AdminUserSummary, isApiError, useResetUserPassword } from "@/lib/api";
import { logger } from "@/lib/logger";

interface UserResetPasswordDialogProps {
  user: AdminUserSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserResetPasswordDialog({
  user,
  open,
  onOpenChange,
}: UserResetPasswordDialogProps) {
  const resetPassword = useResetUserPassword();
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const sending = resetPassword.isPending;

  // Reset transient state whenever the dialog opens for a (new) user.
  useEffect(() => {
    if (open) {
      setError("");
      setSent(false);
    }
  }, [open, user]);

  const handleSend = async () => {
    if (!user) {
      return;
    }
    setError("");

    try {
      await resetPassword.mutateAsync({ userId: user.id });
      setSent(true);
    } catch (err) {
      logger.error("[user-reset-password] failed:", err);
      const detail =
        isApiError(err) && err.body && typeof err.body === "object" && "detail" in err.body
          ? String((err.body as Record<string, unknown>).detail)
          : "Failed to send reset email. Please try again.";
      setError(detail);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Send password reset</AlertDialogTitle>
          <AlertDialogDescription>
            {sent ? (
              <>
                Reset email sent to <strong>{user?.email}</strong>.
              </>
            ) : (
              <>
                Send a password reset email to <strong>{user?.email}</strong>? They&apos;ll receive
                a link to choose a new password.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <AlertDialogFooter>
          {sent ? (
            <AlertDialogAction onClick={() => onOpenChange(false)}>Close</AlertDialogAction>
          ) : (
            <>
              <AlertDialogCancel disabled={sending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                disabled={sending}
              >
                {sending ? "Sending..." : "Send reset email"}
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
